import { CheckCircle2Icon, ArrowRightIcon } from "lucide-react";
import { isFreeUser } from "@/lib/user-type";
import type {
  ConceptAssessmentDTO,
  ConnectedBookResponse,
  GapAnalysisDTO,
  Tier,
  TierVerdictDTO,
  User,
} from "@/types/api";

// =============================================================================
// Public component
// =============================================================================

interface GapAnalysisReportProps {
  gapAnalysis: GapAnalysisDTO;
  assessmentTitle: string;
  answeredCount: number;
  connectedBook: ConnectedBookResponse | null;
  user: User | null;
}

export function GapAnalysisReport({
  gapAnalysis,
  assessmentTitle,
  answeredCount,
  connectedBook,
  user,
}: GapAnalysisReportProps) {
  const variant = deriveVariant(user, connectedBook);
  const annotatedConcepts = annotateConcepts(gapAnalysis.concept_assessments);
  const diagnosticContext = buildDiagnosticContext(
    answeredCount,
    gapAnalysis.concept_assessments.length,
    assessmentTitle
  );

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
      <ResultsTopBar assessmentTitle={assessmentTitle} />

      <h1
        className="text-[26px] sm:text-[28px] font-medium leading-tight tracking-tight"
        style={{ fontFamily: "var(--font-serif, ui-serif, Georgia, serif)" }}
      >
        Here's what we saw in your responses.
      </h1>

      <p
        className="mt-3 leading-relaxed"
        style={{ color: "var(--muted-foreground)", fontSize: "15.5px" }}
      >
        {diagnosticContext}
      </p>

      <TierVerdictLine verdict={gapAnalysis.tier_verdict} />

      {annotatedConcepts.length > 0 && (
        <div className="mt-10">
          <TierScaleHeader />
          <div
            className="border-t"
            style={{ borderColor: "var(--border)" }}
          >
            {annotatedConcepts.map((concept, index) => (
              <ConceptRow
                key={`${concept.concept}-${index}`}
                concept={concept}
                isLast={index === annotatedConcepts.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {variant === "free" && (
        <LabConversionCTA
          gapAnalysis={gapAnalysis}
          connectedBook={connectedBook}
        />
      )}
      {variant === "paid-starting" && connectedBook && (
        <LabContinuation connectedBook={connectedBook} />
      )}
      {variant === "paid-fallback" && <PaidFallbackCTA />}
    </article>
  );
}

// =============================================================================
// Variant routing
// =============================================================================

type ReportVariant = "free" | "paid-starting" | "paid-fallback";

function deriveVariant(
  user: User | null,
  connectedBook: ConnectedBookResponse | null
): ReportVariant {
  if (isFreeUser(user)) return "free";
  if (connectedBook?.role === "starting") return "paid-starting";
  // Any other paid member (finishing/chapter role, or no book linkage) gets a
  // neutral fallback instead of a dead-end empty report.
  return "paid-fallback";
}

// =============================================================================
// Annotation derivation (strength / frontier from observation + tier)
// =============================================================================

const TIER_RANK: Record<Tier, number> = {
  Foundation: 0,
  Working: 1,
  Senior: 2,
  Staff: 3,
};

interface AnnotatedConcept extends ConceptAssessmentDTO {
  annotationKind: "strength" | "frontier" | null;
}

function annotateConcepts(
  conceptAssessments: ConceptAssessmentDTO[]
): AnnotatedConcept[] {
  // Find the highest-tier concept with an observation (strength) and the
  // lowest-tier concept with an observation (frontier). When only one
  // concept has an observation, classify it by which extreme it sits at:
  // Senior+ → strength, otherwise frontier.
  const observed = conceptAssessments
    .map((concept, index) => ({ concept, index }))
    .filter(({ concept }) => Boolean(concept.observation));

  if (observed.length === 0) {
    return conceptAssessments.map((concept) => ({
      ...concept,
      annotationKind: null,
    }));
  }

  const sortedByTier = [...observed].sort(
    (a, b) => TIER_RANK[a.concept.tier] - TIER_RANK[b.concept.tier]
  );
  const highest = sortedByTier[sortedByTier.length - 1];
  const lowest = sortedByTier[0];

  const strengthIndex =
    TIER_RANK[highest.concept.tier] >= TIER_RANK.Senior ? highest.index : null;
  const frontierIndex = lowest.index === highest.index ? null : lowest.index;
  // Single observation case: classify by extreme.
  const singleObservationIndex =
    observed.length === 1 ? observed[0].index : null;
  const singleIsStrength =
    singleObservationIndex !== null &&
    TIER_RANK[conceptAssessments[singleObservationIndex].tier] >=
      TIER_RANK.Senior;

  return conceptAssessments.map((concept, index) => {
    if (singleObservationIndex !== null && index === singleObservationIndex) {
      return {
        ...concept,
        annotationKind: singleIsStrength ? "strength" : "frontier",
      };
    }
    if (index === strengthIndex) {
      return { ...concept, annotationKind: "strength" };
    }
    if (index === frontierIndex) {
      return { ...concept, annotationKind: "frontier" };
    }
    return { ...concept, annotationKind: null };
  });
}

// =============================================================================
// diagnosticContext template
// =============================================================================

function buildDiagnosticContext(
  questionCount: number,
  conceptCount: number,
  assessmentTitle: string
): string {
  const questions = pluralize(questionCount, "question", "questions");
  const concepts = pluralize(conceptCount, "concept", "concepts");
  return `${questions} across ${concepts} in ${assessmentTitle}.`;
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

// =============================================================================
// ResultsTopBar
// =============================================================================

function ResultsTopBar({ assessmentTitle }: { assessmentTitle: string }) {
  return (
    <div
      className="mb-10 flex flex-wrap items-center gap-3 text-[13px]"
      style={{ color: "var(--muted-foreground)" }}
    >
      <CheckCircle2Icon className="size-3.5" />
      <span>Diagnostic complete</span>
      <span aria-hidden>·</span>
      <span>{assessmentTitle}</span>
    </div>
  );
}

// =============================================================================
// TierVerdictLine — clay-highlight the LLM's "{Tier}-level {noun}" phrase
// =============================================================================

// The LLM is instructed (via prompt best-practice rules) to lead the
// headline with `{Tier}-level {noun}` (e.g., "Senior-level depth", "Working-
// level fluency"). The frontend regex-locates that phrase in the headline
// and clay-highlights it. Fallback chain when the LLM drifts:
//   1. Match `{Tier}-level {word}` → highlight the matched phrase.
//   2. No match but tier name appears alone → highlight just the tier name.
//   3. No tier reference at all → render plain ink.
// We do NOT reject non-compliant output; we just decorate what we got.

const TIER_LEVEL_PHRASE_PATTERN = /(Foundation|Working|Senior|Staff)-level\s+\w+/i;
const TIER_NAME_PATTERN = /\b(Foundation|Working|Senior|Staff)\b/i;

function TierVerdictLine({ verdict }: { verdict: TierVerdictDTO }) {
  const headline = verdict.headline?.trim() || `${verdict.tier}-level depth.`;
  const segments = highlightVerdict(headline);

  return (
    <p
      className="mt-12 leading-tight tracking-tight font-medium max-w-3xl"
      style={{
        fontFamily: "var(--font-serif, ui-serif, Georgia, serif)",
        fontSize: "32px",
      }}
    >
      {segments.map((seg, i) =>
        seg.highlight ? (
          <span key={i} style={{ color: "var(--ie-clay)" }}>
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </p>
  );
}

function highlightVerdict(
  headline: string
): Array<{ text: string; highlight: boolean }> {
  const phraseMatch = headline.match(TIER_LEVEL_PHRASE_PATTERN);
  if (phraseMatch && phraseMatch.index !== undefined) {
    const start = phraseMatch.index;
    const end = start + phraseMatch[0].length;
    return [
      { text: headline.slice(0, start), highlight: false },
      { text: phraseMatch[0], highlight: true },
      { text: headline.slice(end), highlight: false },
    ];
  }
  const nameMatch = headline.match(TIER_NAME_PATTERN);
  if (nameMatch && nameMatch.index !== undefined) {
    const start = nameMatch.index;
    const end = start + nameMatch[0].length;
    return [
      { text: headline.slice(0, start), highlight: false },
      { text: nameMatch[0], highlight: true },
      { text: headline.slice(end), highlight: false },
    ];
  }
  return [{ text: headline, highlight: false }];
}

// =============================================================================
// TierScaleHeader (Foundation / Working / Senior / Staff labels above bars)
// =============================================================================

const TIERS: Tier[] = ["Foundation", "Working", "Senior", "Staff"];
const ROW_GRID =
  "minmax(220px, 1.4fr) minmax(0, 3fr) 96px";

function TierScaleHeader() {
  return (
    <div
      className="grid gap-x-6 pb-3"
      style={{ gridTemplateColumns: ROW_GRID }}
    >
      <div></div>
      <div className="relative h-4">
        {TIERS.map((tier, index) => {
          const left = ((index + 0.5) / TIERS.length) * 100;
          return (
            <span
              key={tier}
              className="absolute -translate-x-1/2 uppercase"
              style={{
                left: `${left}%`,
                color: "var(--muted-foreground)",
                letterSpacing: "0.06em",
                fontSize: "11px",
              }}
            >
              {tier}
            </span>
          );
        })}
      </div>
      <div
        className="text-right uppercase"
        style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.06em",
          fontSize: "11px",
        }}
      >
        Your tier
      </div>
    </div>
  );
}

// =============================================================================
// ConceptRow + Bar
// =============================================================================

function ConceptRow({
  concept,
  isLast,
}: {
  concept: AnnotatedConcept;
  isLast: boolean;
}) {
  return (
    <div
      className={`grid items-center gap-x-6 py-5 ${isLast ? "" : "border-b"}`}
      style={{
        gridTemplateColumns: ROW_GRID,
        borderColor: "var(--border)",
      }}
    >
      <div
        className="font-medium leading-snug"
        style={{ fontSize: "15px" }}
      >
        {concept.concept}
      </div>
      <div>
        <Bar position={TIER_RANK[concept.tier]} />
      </div>
      <div
        className="text-right font-medium"
        style={{
          color: "var(--foreground)",
          fontSize: "14px",
        }}
      >
        {concept.tier}
      </div>

      {concept.annotationKind && concept.observation && (
        <div
          className="col-start-2 col-span-2 mt-3 pl-3 leading-relaxed italic"
          style={{
            color: "var(--muted-foreground)",
            borderLeft: `2px solid ${
              concept.annotationKind === "strength"
                ? "var(--ie-clay-rule)"
                : "var(--border)"
            }`,
            fontSize: "13.5px",
          }}
        >
          {concept.annotationKind === "strength" && (
            <span
              className="not-italic mr-2 font-semibold uppercase"
              style={{
                color: "var(--ie-clay)",
                letterSpacing: "0.08em",
                fontSize: "11px",
              }}
            >
              What we noticed
            </span>
          )}
          {concept.observation}
        </div>
      )}
    </div>
  );
}

function Bar({ position }: { position: number }) {
  // Discrete tier centers map to 12.5 / 37.5 / 62.5 / 87.5 %.
  const pct = ((position + 0.5) / TIERS.length) * 100;

  return (
    <div className="relative h-2.5">
      {/* Runway hairline */}
      <div
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-full"
        style={{ height: "2px", background: "var(--border)" }}
      />
      {/* Tier ticks at 25/50/75% */}
      {[25, 50, 75].map((leftPct) => (
        <div
          key={leftPct}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
          style={{
            left: `${leftPct}%`,
            width: "1px",
            height: "8px",
            background: "var(--border)",
          }}
        />
      ))}
      {/* Filled bar from 0 to position */}
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: `${pct}%`,
          height: "6px",
          background: "var(--foreground)",
        }}
      />
      {/* Position dot with knockout halo */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full"
        style={{
          left: `${pct}%`,
          width: "12px",
          height: "12px",
          background: "var(--foreground)",
          boxShadow: "0 0 0 3px var(--background)",
        }}
      />
    </div>
  );
}

// =============================================================================
// LabConversionCTA (free variant)
// =============================================================================

function LabConversionCTA({
  gapAnalysis,
  connectedBook,
}: {
  gapAnalysis: GapAnalysisDTO;
  connectedBook: ConnectedBookResponse | null;
}) {
  const labCTA = gapAnalysis.lab_cta?.trim();
  const buttonLabel = connectedBook?.chapter_number
    ? `Start chapter ${connectedBook.chapter_number} with the Lab`
    : "Start with the Lab";

  return (
    <section
      className="mt-12 pt-10 border-t"
      style={{ borderColor: "var(--border)" }}
    >
      {labCTA && (
        <p
          className="leading-relaxed max-w-3xl"
          style={{ fontSize: "16px" }}
        >
          {labCTA}
        </p>
      )}

      <p
        className="mt-8"
        style={{ color: "var(--muted-foreground)", fontSize: "15px" }}
      >
        Each week the Lab gives you:
      </p>
      <ul className="mt-3 space-y-2 leading-relaxed" style={{ fontSize: "15px" }}>
        {[
          "a reading map for the chapter, what to read deeply, what to skim, what to skip",
          "an assessment that checks whether you can apply the chapter, not just recall it",
          "a Discord where senior engineers debate edge cases against each other",
        ].map((bullet) => (
          <li key={bullet} className="flex gap-3">
            <span
              className="mt-[10px] shrink-0 rounded-full"
              style={{
                width: "5px",
                height: "5px",
                background: "var(--foreground)",
              }}
            />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      <figure
        className="mt-10 max-w-2xl pl-5 border-l"
        style={{ borderColor: "var(--border)" }}
      >
        <blockquote
          className="leading-relaxed"
          style={{
            fontFamily: "var(--font-serif, ui-serif, Georgia, serif)",
            fontSize: "15.5px",
          }}
        >
          &ldquo;This was exactly what I needed. The quizzes are excellent for
          checking understanding and applying theory.&rdquo;
        </blockquote>
        <figcaption
          className="mt-2"
          style={{ color: "var(--muted-foreground)", fontSize: "13px" }}
        >
          Vlad, SWE @ Google
        </figcaption>
      </figure>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <a
          href="https://irreplaceable.engineer/lab/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-xl font-semibold transition hover:opacity-90 active:translate-y-px"
          style={{
            background: "var(--foreground)",
            color: "var(--background)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
            padding: "14px 24px",
            fontSize: "15px",
          }}
        >
          {buttonLabel}
          <ArrowRightIcon className="ml-2 size-4" />
        </a>
        <span
          style={{ color: "var(--muted-foreground)", fontSize: "13.5px" }}
        >
          $49/mo · cancel anytime
        </span>
      </div>
    </section>
  );
}

// =============================================================================
// LabContinuation (paid-starting variant)
// =============================================================================

function LabContinuation({
  connectedBook,
}: {
  connectedBook: ConnectedBookResponse;
}) {
  const chapterRef = connectedBook.chapter_number
    ? `chapter ${connectedBook.chapter_number}`
    : "the next chapter";
  const bookHref = `/books/${connectedBook.book_id}`;

  return (
    <section
      className="mt-12 pt-10 border-t"
      style={{ borderColor: "var(--border)" }}
    >
      <p className="leading-relaxed max-w-3xl" style={{ fontSize: "16px" }}>
        You're set up to start{" "}
        <span className="font-medium">{connectedBook.book_title}</span>
        {connectedBook.chapter_title
          ? ` with ${chapterRef}: ${connectedBook.chapter_title}.`
          : "."}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a
          href={bookHref}
          className="inline-flex items-center justify-center rounded-xl font-semibold transition hover:opacity-90 active:translate-y-px"
          style={{
            background: "var(--foreground)",
            color: "var(--background)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
            padding: "14px 24px",
            fontSize: "15px",
          }}
        >
          Start reading
          <ArrowRightIcon className="ml-2 size-4" />
        </a>
      </div>
    </section>
  );
}

// =============================================================================
// PaidFallbackCTA (paid-fallback variant — non-starting or unlinked assessment)
// =============================================================================

function PaidFallbackCTA() {
  return (
    <section
      className="mt-12 pt-10 border-t"
      style={{ borderColor: "var(--border)" }}
    >
      <p className="leading-relaxed max-w-3xl" style={{ fontSize: "16px" }}>
        That's your diagnostic. Head back to your dashboard to keep going.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a
          href="/dashboard"
          className="inline-flex items-center justify-center rounded-xl font-semibold transition hover:opacity-90 active:translate-y-px"
          style={{
            background: "var(--foreground)",
            color: "var(--background)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
            padding: "14px 24px",
            fontSize: "15px",
          }}
        >
          Back to dashboard
          <ArrowRightIcon className="ml-2 size-4" />
        </a>
      </div>
    </section>
  );
}
